import { PaymentCodes } from '@constants/app.constants';
import { OversizedBagAncillarySsrs, OversizedBagSsrs, StandardBagAncillarySsrs, StandardBagSsrs } from '@constants/bags.constants';
import { CabinProductClass } from '@constants/cabin.constants';
import { ExtrasSsrsEnum } from '@constants/extras.constants';
import {
  MealFirstServiceSsrEnum,
  MealSecondServiceSsrEnum,
} from '@constants/meals.constants';
import { SeatClass } from '../constants/home.constants';
import { NullableString } from './app.models';
import {
  PassengerAddress,
  PassengerInfant,
  AssistanceServices,
  PassengerTravelDocument,
} from './requests/passenger-update-request.model';

import { TransportationDesignator } from './responses/availability-response.model';
import { BookingType } from '@constants/booking.constants';

export interface ServiceCharge {
  amount: number;
  code: string | null;
  collectType: number;
  currencyCode: string;
  detail: string;
  foreignAmount: number;
  foreignCurrencyCode: string;
  ticketCode: string;
  type: number;
}
export interface PassengerFee {
  code: string;
  createdDate: Date;
  detail: string;
  flightReference: string;
  isConfirmed: boolean;
  isProtected: boolean;
  note: string;
  override: true;
  paid: boolean;
  passengerFeeKey: string;
  paymentNumber: number;
  serviceCharges: ServiceCharge[];
  ssrCode: string;
  ssrNumber: number;
  totalAmount: number;
  totalTax: number;
  type: number;
}

export interface PassengerInfo {
  birthDay: string | number | null;
  birthMonth: string | number | null;
  birthYear: string | number | null;
  dateOfBirth: string | null;
  familyNumber: string | null;
  gender: number;
  nationality: string | null;
  residentCountry: string | null;
}
export interface Passenger {
  addresses?: PassengerAddress[];
  assistanceServices?: AssistanceServices;
  bags?: PassengerBag[];
  customerNumber?: number;
  discountCode?: string;
  fees?: PassengerFee[];
  infant?: PassengerInfant;
  info: PassengerInfo;
  journeysInfo?: N0PassengerJourneyInfo[];
  name: Name | null;
  passengerAlternateKey?: string;
  passengerKey: string;
  passengerTypeCode?: PassengerTypeCode;
  program?: string;
  travelDocuments?: PassengerTravelDocument[];
}

export interface N0PassengerJourneyInfo {
  journeyKey: string;
  ssrs: string[];
}

export enum SsrsOfValue {
  CHECK_IN_ATTEMPT = 'CKNA',
  ONE_WAY_TO_US = 'RTJR',
}

export enum PassengerTypeCode {
  ADT = 'ADT',
  CHD = 'CHD',
  INF = 'INF',
  INS = 'INS',
  OTN = 'OTN',
  TEN = 'TEN',
  YTN = 'YTN',
}

export const CHILD_PASSENGER_TYPE_CODES = [
  PassengerTypeCode.CHD,
  PassengerTypeCode.INS,
  PassengerTypeCode.OTN,
  PassengerTypeCode.TEN,
];

export interface PassengerAgeRange {
  code: PassengerTypeCode;
  maxAge: number;
  minAge: number;
}

export enum TaxTypeCode {
  AY = 'AY',
  FR = 'FR',
  G2 = 'G2',
  IZ = 'IZ',
  O4 = 'O4',
  QX = 'QX',
  US = 'US',
  XA = 'XA',
  XF = 'XF',
  YC = 'YC',
  ZN = 'ZN',
}

export interface Address {
  city?: string;
  countryCode?: string;
  lineOne?: string;
  lineThree?: string;
  lineTwo?: string;
  postalCode?: string;
  provinceState?: string;
}
export interface PassengerCountSeatClass {
  adultCount: number;
  childCount: number;
  seatClass: SeatClass;
}
export interface JourneyBags {
  designator?: FlightDesignator | undefined;
  journeyKey: string;
  passengerBags: PassengerBag[];
  productClass?: CabinProductClass;
}

export interface JourneyExtras {
  journeyKey: string;
  passengerExtras: PassengerExtra[];
}

export interface PassengerExtra {
  extras: PassengerSsr[];
  passengerKey: string;
}

export interface PassengerBag {
  bags: PassengerSsr[];
  passengerKey: string;
}
export interface PassengerSsr {
  inBundleCount: number;
  paidCount: number;
  ssrCode:
    | StandardBagSsrs
    | OversizedBagSsrs
    | StandardBagAncillarySsrs
    | OversizedBagAncillarySsrs
    | ExtrasSsrsEnum
    | MealFirstServiceSsrEnum
    | MealSecondServiceSsrEnum;
  ssrs: Ssr[];
  totalCount: number;
  totalPrice: number;
}
export interface Ssr {
  currencyCode: string;
  inBundle: boolean;
  paid: boolean;
  price: number;
  ssrCode: string;
}
export interface BookingBreakdownJourneyTotals {
  totalAmount: number;
  totalDiscount: number;
  totalPoints: number;
  totalTax: number;
}

export interface ChargeBreakdown {
  total: number;
}
export interface BookingBreakdownPassengerTotals {
  convenience?: ChargeBreakdown;
  infant?: ChargeBreakdown;
  seats: ChargeBreakdown;
  services?: ChargeBreakdown;
  specialServices: ChargeBreakdown;
  upgrades?: ChargeBreakdown;
}

export interface BookingBreakdownPassenger {}

export interface BookingBreakdownJourney {
  journeyKey: string;
  totalAmount: number;
  totalDiscount: number;
  totalPoints: number;
  totalTax: number;
}

export interface BookingBreakdown {
  addOnTotals?: AddOnPriceBreakdown;
  authorizedBalanceDue?: number;
  balanceDue: number;
  journeys?: { [key: string]: BookingBreakdownJourney };
  journeyTotals: BookingBreakdownJourneyTotals;
  passengers?: { [key: string]: BookingBreakdownPassenger };
  passengerTotals: BookingBreakdownPassengerTotals;
  pointsBalanceDue?: number;
  totalAmount?: number;
  totalCharged?: number;
  totalPoints?: number;
  totalPointsToCollect?: number;
  totalToCollect?: number;
}

export interface N0TripBreakdown {
  authorizedBalanceDue?: number;
  balanceDue: number;
  balanceDueStrikethrough: number;
  categoryTotals: N0CategoryTotal;
  journeyBreakdowns: N0JourneyBreakdown[];
  paymentsTotal: number;
  pointsBalanceDue?: number;
  totalAmount?: number;
  totalCharged?: number;
  totalPoints?: number;
  totalPointsToCollect?: number;
  totalToCollect?: number;
}

export interface N0JourneyBreakdown {
  categoryBreakdown: N0CategoryTotal;
  journeyKey: string;
  journeyTotal: number;
  journeyTotalStrikethrough: number;
}

export interface N0CategoryTotal {
  bags: N0CategoryBreakdown | null;
  extras: N0CategoryBreakdown | null;
  fares: N0CategoryFareBreakdown;
  meals: N0CategoryBreakdown | null;
  other: N0CategoryBreakdown | null;
  seats: N0CategoryBreakdown | null;
  spoilage: N0CategoryBreakdown | null;
}

export interface N0CategoryBreakdown {
  details: N0CategoryDetail[];
  totalPrice: number;
}

export interface N0CategoryFareBreakdown {
  fareDetails: N0CategoryFareDetail[];
  strikethroughTotalPrice: number;
  totalPrice: number;
}

export interface N0CategoryFareDetail {
  details: N0CategoryDetail[];
  passengerType: string;
  strikethroughTotalPrice: number;
  totalCount: number;
  totalPrice: number;
}

export interface N0CategoryDetail {
  code: string;
  totalCount: number;
  totalPrice: number;
}

export interface N0CommitRequest {
  bookingFlow: string;
  commitRequest?: CommitRequestV2;
}

export interface CommitRequestV2 {
  comments: BookingCommentRequest[];
  contactTypesToNotify: string[];
  hold: BookingHold;
  notifyContacts: boolean;
  receivedBy: string;
  restrictionOverride: boolean;
}

export interface BookingCommentRequest {
  createdDate: Date;
  sendToBookingSource: boolean;
  text: string;
  type: CommentType;
}

export enum CommentType {
  Default = 0,
  Itinerary = 1,
  Manifest = 2,
  Alert = 3,
  Archive = 4,
}

export interface AddOnPriceBreakdown {
  activities: number;
  car: number;
  hotel: number;
}
export interface Extras {
  journeyKey: string;
  passengerExtras: PassengerExtras[];
}
export interface PassengerExtras {
  extras: PassengerSsr[];
  passengerKey: string;
}
export interface Meals {
  legKey: string;
  passengerMeals: PassengerMeals[];
}
export interface PassengerMeals {
  firstService: MealService[];
  passengerKey: string;
  secondService: MealService[];
}
export interface MealService {
  inBundleCount: number;
  paidCount: number;
  ssrCode: string;
  ssrs: MealSsr[];
  totalCount: number;
  totalPrice: number;
}
export interface MealSsr {
  currencyCode: string;
  inBundle: boolean;
  paid: boolean;
  price: number;
  ssrCode: string;
}
export interface Booking {
  addOns?: { [key: string]: AddOn };
  bags?: JourneyBags[];
  bookingKey: NullableString;
  breakdown: BookingBreakdown;
  contacts: { [key: string]: Contact };
  currencyCode?: string;
  details: N0BookingDetails;
  extras?: JourneyExtras[];
  hold: BookingHold;
  info: BookingInfo;
  journeys: N0Journey[];
  locators: RecordLocators;
  meals?: Meals[];
  orders?: BookingOrder[];
  other?: JourneyOtherFee[];
  passengers: { [key: string]: Passenger };
  payments: Payment[];
  receivedBy?: ReceivedBy;
  recordLocator: NullableString;
  seats?: Seat[];
  spoilage?: PassengerSpoilageFees[];
  systemCode?: string;
  tripBreakdown: N0TripBreakdown;
  typeOfSale: TypeOfSale;
}

export interface PassengerSpoilageFees {
  fees: Fee[];
  passengerKey: NullableString;
}

export interface JourneyOtherFee {
  journeyKey: string | null,
  otherFees: OtherFee[] | null;
}

export interface OtherFee {
  fees: Fee[];
}

export interface Fee {
  code: string;
  paid: boolean;
  price: number;
}

export interface Meals {
  legKey: string;
  passengerMeals: PassengerMeals[];
}
export interface PassengerMeals {
  firstService: MealService[];
  passengerKey: string;
  secondService: MealService[];
}
export interface MealService {
  inBundleCount: number;
  paidCount: number;
  ssrCode: string;
  ssrs: MealSsr[];
  totalPrice: number;
}
export interface MealSsr {
  currencyCode: string;
  inBundle: boolean;
  paid: boolean;
  price: number;
  ssrCode: string;
}

export interface BookingOrder {
  bookingOrderKey: string;
  collect: number;
  status: BookingOrderStatus;
  thirdPartyCharge: number;
  thirdPartyHold: number;
  total: number;
}

export enum BookingOrderStatus {
  None = 0,
  FinalizedProcess = 1,
}
export interface AddOn {
  addOnKey: string;
  created: CreatedAddOnDetails;
  cultureCode: string;
  declinedText: string;
  isHistorical: boolean;
  modifiedAgentReference: string;
  modifiedDate: Date;
  order: Order;
  paymentRequired: boolean;
  reference: string;
  source: AddOnDetails;
  status: AddOnStatus;
  summary: AddOnOrderSummary;
  type: ProductTypeCode;
}
export interface Order {
  phoneNumbers: PhoneNumber[];
}
export interface OrderBase {
  Active: boolean;
  Description: string;
  DescriptionFormatType: string;
  ExternalLocator: string;
  OrderKey: string;
  ProductDescription: string;
  ProductOrderKey: string;
  ProductVariationDescription: string;
  ProductVariationKey: string;
  Quantity: number;
  ThumbnailFileName: string;
  UsageDate: Date;
}
export interface CreatedAddOnDetails extends AddOnDetails {
  agentReference: string;
  date: Date;
}
export interface AddOnDetails {
  agentCode: string;
  domainCode: string;
  locationCode: string;
  organizationCode: string;
}

export enum AddOnStatus {
  Unknown = 0,
  Active = 1,
  Pending = 2,
  Confirming = 3,
  CancelPending = 4,
  Cancelled = 5,
}

export enum ProductTypeCode {
  Default = 0,
  Insurance = 1,
  Activity = 2,
  Hotel = 3,
  Car = 4,
}
export interface AddOnOrderSummary {
  BeginDate: Date;
  BeginLocation: string;
  Charged: number;
  Description: string;
  EndDate: Date;
  EndLocation: string;
  ExternalConfirmationNumber: string;
  ExternalReference: string;
  Held: number;
  SupplierCode: string;
  Total: number;
}
export interface Contact {
  companyName: string;
  contactTypeCode: string;
  cultureCode: string;
  customerNumber: string;
  distributionOption: DistributionOption;
  emailAddress: string;
  name: Name;
  notificationPreference: NotificationPreference;
  phoneNumbers: PhoneNumber[];
  sourceOrganization: string;
}
export interface Name {
  first: string;
  last: string;
  middle?: NullableString;
  suffix?: NullableString;
  title?: NullableString;
}

export enum Gender {
  XX = 0,
  Male = 1,
  Female = 2,
}

export enum NotificationPreference {
  None = 0,
  Promotional = 1,
}
export interface PhoneNumber {
  number: string;
  type: PhoneNumberType;
}

export enum PhoneNumberType {
  Other = 0,
  Home = 1,
  Work = 2,
  Mobile = 3,
  Fax = 4,
}

export enum DistributionOption {
  None = 0,
  Mail = 1,
  Email = 2,
  Fax = 3,
  MailFax = 4,
  Airport = 5,
  Hold = 6,
  Print = 7,
}
export interface ReceivedBy {
  latestReceivedBy: string;
  latestReceivedReference: string;
  receivedBy: string;
  receivedReference: string;
  referralCode: string;
}
export interface BookingHold {
  expiration: Date;
}
export interface TypeOfSale {
  fareTypes: string[];
  promotionCode: string;
  residentCountry: string;
}
export interface BookingSales {
  created: PointOfSale;
  modified: PointOfSale;
  source: BookingPointOfSale;
}
export interface BookingPointOfSale extends PointOfSale {
  isoCountryCode: string;
  sourceSystemCode: string;
}
export interface PointOfSale {
  agentCode?: string;
  domainCode?: string;
  locationCode?: string;
  organizationCode?: string;
}
export interface RecordLocators {
  numericRecordLocator: string;
  parentId: number;
  parentRecordLocator: string;
  recordLocators: RecordLocator[];
}
export interface RecordLocator {
  interactionPurpose: string;
  owningSystemCode: string;
  recordCode: string;
  recordLocatorKey: string;
}
export interface BookingInfo {
  bookedDate: Date;
  bookingType: string;
  changeAllowed: boolean;
  channelType: ChannelType;
  createdAgentId: number;
  createdDate: Date;
  expirationDate: Date;
  modifiedAgentId: number;
  modifiedDate: Date;
  owningCarrierCode: string;
  paidStatus: PaidStatus;
  priceStatus: PriceStatus;
  profileStatus: BookingProfileStatus;
  status: BookingStatus;
}

export enum ChannelType {
  Default = 0,
  Direct = 1,
  Web = 2,
  Gds = 3,
  Api = 4,
  DigitalApi = 5,
  DigitalWeb = 6,
  Ndc = 7,
}

export enum BookingStatus {
  Default = 0,
  Hold = 1,
  Confirmed = 2,
  Closed = 3,
  HoldCanceled = 4,
  PendingArchive = 5,
  Archived = 6,
}

export enum PaidStatus {
  UnderPaid = 0,
  PaidInFull = 1,
  OverPaid = 2,
}

export enum PriceStatus {
  Invalid = 0,
  Override = 1,
  Valid = 2,
}

export enum BookingProfileStatus {
  Default = 0,
  KnownIndividual = 1,
  ResolutionGroup = 2,
  SelecteeGroup = 3,
  NotUsed = 4,
  FailureGroup = 5,
  RandomSelectee = 6,
  Exempt = 7,
}

export interface Flight {}
export interface Payment {
  accountId: number;
  addedToState?: boolean;
  amounts?: PaymentAmounts;
  approvalDate?: string;
  authorizationCode?: string;
  channelType?: ChannelType;
  code: PaymentCodes;
  createdAgentId?: number;
  createdDate?: string;
  deposit?: boolean;
  details?: PaymentDetails;
  fundedDate?: string;
  modifiedAgentId?: number;
  modifiedDate?: string;
  paymentKey: string;
  pointOfSale?: PointOfSale;
  reference?: number;
  sourcePointOfSale?: PointOfSale;
  status: BookingPaymentStatus;
  transactionCode?: string;
  transferred?: boolean;
  type?: PaymentMethodType;
}

export enum BookingPaymentStatus {
  New = 0,
  Received = 1,
  Pending = 2,
  Approved = 3,
  Declined = 4,
  Unknown = 5,
  PendingCustomerAction = 6,
}

export interface PaymentDetails {
  accountName: string;
  accountNumber: string;
  accountNumberId: number;
  binRange: number;
  expirationDate: Date;
  fields: { [key: string]: string };
  installments: number;
  parentPaymentId: number;
  text: string;
}

export interface PaymentAmounts {
  amount: number;
  collected: number;
  collectedCurrencyCode: string;
  currencyCode: string;
  quoted: number;
  quotedCurrencyCode: string;
}

export enum PaymentMethodType {
  ExternalAccount = 0,
  PrePaid = 1,
  AgencyAccount = 2,
  CustomerAccount = 3,
  Voucher = 4,
  Loyalty = 5,
}

export interface SeatMapAvailability {
  fees: { [passengerKey: string]: PassengerSeatGroupInfo | undefined };
  seatMap: SeatMap;
  sitThreeSsr: string;
  sitTogetherEconomyCabin: SitTogether;
  sitTogetherPremiumCabin: SitTogether;
  sitTwoSsr: string;
}

export interface SeatMapAvailabiliyWithRows extends SeatMapAvailability {
  compartments: CompartmentInformationWithRows[];
}

export interface PassengerSeatGroupInfo {
  groups: { [key: number]: SeatGroupInfo };
  passengerKey: string;
}

export interface SeatGroupInfo {
  fees: PassengerFee[];
  group: number;
}

export interface CompartmentInformationWithRows extends CompartmentInformation {
  columnsCount: number;
  rows: RowInformation[];
  rowSpacing: number;
  totalHeightInRows: number;
}

export interface RowInformation {
  designator: string;
  order: number;
  rowOffset: number;
  units: UnitInformation[];
  width: number;
}

export interface SeatMap {
  arrivalStation: string;
  availableUnits: number;
  decks: { [key: string]: DeckInformation };
  departureStation: string;
  equipmentType: string;
  equipmentTypeSuffix: string;
  marketingCode: string;
  name: string;
  seatmapReference: string;
}

export interface DeckInformation {
  compartments: { [key: string]: CompartmentInformation };
  number: number;
}

export interface CompartmentInformation {
  availableUnits: number;
  designator: string;
  length: number;
  orientation: number;
  sequence: number;
  units: UnitInformation[];
  width: number;
}

export interface UnitInformation {
  assignable: boolean;
  availability: SeatAvailability;
  compartmentDesignator: string;
  designator: string;
  group: number;
  height: number;
  properties: { code: string; value: string }[];
  text: string;
  travelClassCode: string;
  type: UnitType;
  unitKey: string | null;
  width: number;
  x: number;
  y: number;
}

export enum SeatAvailability {
  Unknown = 0,
  Reserved = 1,
  Blocked = 2,
  HeldForAnotherSession = 3,
  HeldForThisSession = 4,
  Open = 5,
  Missing = 6,
  CheckedIn = 7,
  FleetBlocked = 8,
  Restricted = 9,
  Broken = 10,
  ReservedForPnr = 11,
  SoftBlocked = 12,
  Unavailable = 13,
}

export enum UnitType {
  None = 0,
  NormalSeat = 1,
  LargeSeat = 2,
  Couchette = 3,
  Compartment = 4,
  Table = 5,
  Wall = 6,
  Window = 7,
  Door = 8,
  Stairs = 9,
  Wing = 10,
  Other = 12,
  Bulkhead = 13,
  BedOneOfThree = 14,
  BedTwoOfThree = 15,
  BedThreeOfThree = 16,
  BedOneOfTwo = 17,
  BedTwoOfTwo = 18,
  Bed = 19,
  Exit = 20,
  LabelRuler = 21,
  GenericUnitResizableArea = 22,
  Lavatory = 23,
  LavatoryWithHandicappedFacilities = 24,
  Luggage = 25,
  MovableCompartmentDivider = 26,
  Bar = 27,
  Closet = 28,
  Galley = 29,
  MovieScreen = 30,
  Storage = 31,
  Aisle = 99,
}
export interface Seat {
  journeyKey: string;
  passengerSeats: PassengerSeat[];
  seatSsr: PassengerSeatSsr[];
  segmentKey: string;
}

export interface SitTogether {
  sitThree: boolean,
  sitTwo: boolean
}

export interface StationAdvisory {}

export interface PassengerSegmentSeatInformation {
  deck: number;
  propertyList: { [key: string]: string };
  seatSet: number;
}
export interface PassengerSeat {
  arrivalStation?: string;
  compartmentDesignator?: string;
  departureStation?: string;
  flightReference?: string;
  journeyKey?: string;
  passengerKey: string;
  seatFees?: Fee[];
  segmentKey?: string;
  total?: number;
  unitDesignator: string;
  unitKey?: string;
}
export interface PassengerSeatSsr {
  passengerKey: string;
  seats: PassengerSsr[];
}

export interface PassengerSegment {
  seats: PassengerSeat[];
}

export type PassengerWithBagsCount = {
  [passengerKey: string]: number;
};

export const BagsSsrEnum = {
  ...StandardBagSsrs,
  ...OversizedBagSsrs,
};

export type BagsSsrEnum = StandardBagSsrs | OversizedBagSsrs | StandardBagAncillarySsrs | OversizedBagAncillarySsrs;

export interface CostAndCount {
  cost: number;
  count: number;
}

export type BagsCostAndCountBySsr = {
  [ssrCode in BagsSsrEnum]: CostAndCount;
};

export type FirstServiceMealsCountBySsr = {
  [ssrCode in MealFirstServiceSsrEnum]: number;
};

export type SecondServiceMealsCountBySsr = {
  [ssrCode in MealSecondServiceSsrEnum]: number;
};

export interface PassengerWithMealsCountBySsr {
  firstService: FirstServiceMealsCountBySsr;
  passengerKey: string;
  secondService: SecondServiceMealsCountBySsr;
}

export interface MealsCostAndCountBySsr {
  firstService: FirstServiceMealsCostAndCountBySsr;
  secondService: SecondServiceMealsCostAndCountBySsr;
}

export type FirstServiceMealsCostAndCountBySsr = {
  [ssrCode in MealFirstServiceSsrEnum]: CostAndCount;
};

export type SecondServiceMealsCostAndCountBySsr = {
  [ssrCode in MealSecondServiceSsrEnum]: CostAndCount;
};

export type ExtrasCostAndCountBySsr = {
  [ssrCode in ExtrasSsrsEnum]: CostAndCount;
};

export type ExtrasCountBySsr = {
  [ssrCode in ExtrasSsrsEnum]: number;
};

export interface PassengerWithExtrasCountBySsr {
  extras: ExtrasCountBySsr;
  passengerKey: string;
}

export interface FlightIdentifier {
  carrierCode: string;
  identifier: string;
  opSuffix?: string;
}
export interface FlightDesignator {
  arrival: string;
  departure: string;
  destination: string;
  origin: string;
}
export interface Segment {
  cabin: string;
  designator: FlightDesignator;
  fares?: SegmentFare[];
  flightReference?: string;
  identifier: FlightIdentifier;
  journeyKey: string;
  legs: Leg[];
  passengerSegment: { [key: string]: PassengerSegment };
  productClass: CabinProductClass;
  segmentKey: string;
  travelTime: number;
}

export interface SegmentFare {
  fareClassOfService?: string;
  passengerFares: PassengerFare[];
  travelClassCode?: string;
  productClass?: CabinProductClass;
}

export interface PassengerFare {
  passengerType: PassengerTypeCode;
  serviceCharges: ServiceCharge[];
}

export interface SegmentWithJourneyKey extends Segment {
  journeyKey: string;
}
export interface Leg {
  arrivalTime: string;
  departureTime: string;
  designator: FlightDesignator;
  equipmentType: string;
  equipmentTypeSuffix: string;
  flightReference: string;
  legKey: string;
  operatedBy: string;
  seatmapReference: string;
  subjectToGovtApproval: boolean;
  travelTime: number;
}
export interface N0Journey {
  cabin: string;
  designator: FlightDesignator;
  flightType: number;
  hasCancelledLeg: boolean;
  journeyKey: string;
  operatedByText: string;
  productClass: string;
  segments: Segment[];
  stops: number;
  subjectToGovtApproval: boolean;
  totalTravelTime: number;
}
export interface FlightPrice {
  total: number;
}

export interface BagsPrice {
  total: number;
}

export interface SeatsPrice {
  total: number;
}

export interface GovernmentCutPrice {
  total: number;
}
export interface PriceDisplay {
  bags: BagsPrice;
  flightPrice: FlightPrice;
  governmentCut: GovernmentCutPrice;
  seats: SeatsPrice;
}
export interface N0BookingDetails {
  canCancel: boolean;
  canChangeTravelers: boolean;
  canEditPassports: boolean;
  hasAnyFlightFlown: boolean;
  isAPG: boolean;
  isDohop: boolean;
  isID90HK: boolean;
  isID90HL: boolean;
  isInternational: boolean;
  isMYIDHK: boolean;
  isMYIDHL: boolean;
  isWithinDot: boolean;
  journeyCheckinRequirements: N0JourneyCheckinRequirements[];
  journeyDetail: N0JourneyDetail[];
  tripInsurancePolicy: NullableString;
  tripType: string;
}

export interface N0JourneyCheckinRequirements {
  checkinRequirements: CheckinRequirements;
  journeyKey: string;
}

export interface CheckinRequirements {
  governmentProgramRequirements: GovernmentProgramRequirements;
  isValid: boolean;
  passengers: CheckinPassengerRequirements;
  restrictions: number[];
}

export interface GovernmentProgramRequirements {
  additionalDataRequired: number[];
  governmentInstance: string;
  requiredDocumentFields: number[];
}

export interface CheckinPassengerRequirements {
  addressRequirements: CheckinAddressRequirements;
  documentRequirements: CheckinDocumentRequirements;
  invalidSsrs: string[];
  isValid: boolean;
  passengerKey: string;
  requiredSsrs: CheckinMissingSegmentRequiredSsr[];
  restrictions: CheckinPassengerRestriction[];
}

export interface CheckinPassengerRestriction {
  description: string;
  restrictionCheckinPassengerRestrictionType: number;
}

export interface CheckinMissingSegmentRequiredSsr {
  codes: string[];
  designator: TransportationDesignator;
}

export interface CheckinAddressRequirements {
  addressesOnFile: string[];
  isValid: boolean;
  unhandledAddresses: string[];
}

export interface CheckinDocumentRequirements {
  documentsOnFile: string[];
  isValid: boolean;
  unhandledDocuments: GovernmentPassengerDocumentRequirement[];
}

export interface GovernmentPassengerDocumentRequirement {
  eligibleDocuments: GovernmentDocumentRequirement;
  governmentInstance: string;
  numberOfMissingDocuments: number;
}

export interface GovernmentDocumentRequirement {
  documentTypeCode: string;
  group: number;
}

export interface N0JourneyDetail {
  canCheckin: boolean;
  canModifyFlight: boolean;
  canModifyJourney: boolean;
  canSellBags: boolean;
  canSellExtras: boolean;
  canSellMeals: boolean;
  canSellSeats: boolean;
  canShowBoardingPass: boolean;
  gateCloses: string;
  inAirportCheckinWindow: boolean;
  inCheckinWindow: boolean;
  isCanceled: boolean;
  isEligibleforOnlineCheckin: boolean;
  isFlown: boolean;
  isWithin24hours: boolean;
  journeyDisplayStatus: N0JourneyDisplayStatus;
  journeyKey: string;
  restrictBagsToCheckin: boolean;
}
export interface N0CalendarInvite {
  bookingDate: string;
  bookingDateFormatted: string;
  contentType: string;
  fileName: string;
  flightNumber: string;
  icsCode: string;
  journeys: N0CalendarJourney[];
  modificationCount: string;
  recordLocator: string;
  utcNow: string;
  utcNowFormatted: string;
}

export interface N0CalendarJourney {
  arrivalStationCode: string;
  arrivalStationName: string;
  arriveTime: string;
  arrivingDateFormatted: string;
  departingDateFormatted: string;
  departStationCode: string;
  departStationName: string;
  departTime: string;
  endTimeZone: string;
  endUtc: string;
  eventSubject: string;
  flightNumber: string;
  journeyDepartDateFormatted: string;
  segments: N0CalendarSegment[];
  startTimeZone: string;
  startUtc: string;
}

export interface N0CalendarSegment {
  legs: N0CalendarLeg[];
}

export interface N0CalendarLeg {
  timeZoneArriving: string;
  timeZoneDeparting: string;
}

export interface BookingPaymentResult {
  paymentKey: string;
  paymentStatus: BookingPaymentStatus;
  redirectUrl3ds: string;
}

export interface CheckInPassengers {
  [passengerKey: string]: boolean;
}

export enum N0JourneyDisplayStatus {
  CheckinNotOpenYet,
  CheckinOpen,
  CheckinClosed,
  IsFlown,
}

export interface BookingAnyJourneySellStatuses {
  canSellBags: boolean;
  canSellExtras: boolean;
  canSellMeals: boolean;
  canSellSeats: boolean;
}

export interface JourneySummary {
  journeyBreakdown: N0JourneyBreakdown[];
  journeyBreakdownObject: { [key: string]: N0JourneyBreakdown };
  journeyTotal: number;
  journeyType: BookingType;
}
